import { supabase } from '@/utils/supabase';
import type { EmailOtpType } from '@supabase/supabase-js';

/**
 * Établit une session Supabase à partir d'une URL de deep link auth
 * (ex. repas://reset-password?code=... après un lien de récupération).
 *
 * Gère les deux formats émis par GoTrue :
 *  - flow PKCE : `?code=...` → exchangeCodeForSession (le code n'est
 *    échangeable qu'avec le verifier stocké localement) ;
 *  - flow implicite hérité : `#access_token=...&refresh_token=...` → setSession
 *    (liens envoyés avant le passage de l'app en PKCE).
 *
 * Retourne true si une session a été établie depuis l'URL, false si l'URL ne
 * contenait aucun paramètre d'auth. Jette si GoTrue renvoie une erreur
 * (lien expiré, code déjà consommé…).
 */
export async function createSessionFromUrl(url: string): Promise<boolean> {
  const hashIndex = url.indexOf('#');
  const fragment = hashIndex >= 0 ? url.slice(hashIndex + 1) : '';
  const queryIndex = url.indexOf('?');
  const query =
    queryIndex >= 0
      ? url.slice(queryIndex + 1, hashIndex >= 0 ? hashIndex : undefined)
      : '';

  const queryParams = new URLSearchParams(query);
  const fragmentParams = new URLSearchParams(fragment);

  const errorDescription =
    queryParams.get('error_description') ?? fragmentParams.get('error_description');
  if (errorDescription) {
    throw new Error(errorDescription.replace(/\+/g, ' '));
  }

  const code = queryParams.get('code');
  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
    return true;
  }

  const tokenHash = queryParams.get('token_hash');
  if (tokenHash) {
    const type = queryParams.get('type') as EmailOtpType | null;
    if (!type) throw new Error('Type de lien de sécurité manquant');
    const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
    if (error) throw error;
    return true;
  }

  const accessToken = fragmentParams.get('access_token');
  const refreshToken = fragmentParams.get('refresh_token');
  if (accessToken && refreshToken) {
    const { error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) throw error;
    return true;
  }

  return false;
}
